let globalAnimationId = "";
abstract class BaseAudioVisualizer {
  protected canvas: HTMLCanvasElement;
  protected ctx: CanvasRenderingContext2D;
  protected audioContext: AudioContext;
  protected analyser: AnalyserNode | null = null;
  protected isActive: boolean = false;
  protected volumeData: number[] = [];
  protected animationId: number | null = null;
  protected id: string;

  constructor(canvasElement: HTMLCanvasElement) {
    this.id = new Date().getTime().toString();
    this.canvas = canvasElement;
    this.ctx = this.canvas.getContext("2d")!;

    // // 调整 Canvas 分辨率
    // dpr = window.devicePixelRatio || 1;

    // const rect = this.canvas.getBoundingClientRect();
    // this.canvas.width = rect.width * dpr;
    // this.canvas.height = rect.height * dpr;
    // this.canvas.style.width = `${rect.width}px`;
    // this.canvas.style.height = `${rect.height}px`;
    // this.ctx.scale(
    //   (rect.width / rect.width) * dpr,
    //   (rect.height / rect.height) * dpr
    // );

    this.audioContext = new window.AudioContext();

    // 启用抗锯齿以获得更平滑的边缘
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = "high";
  }

  protected abstract setupAudioSource(): Promise<void>;

  public async start(): Promise<void> {
    try {
      globalAnimationId = this.id;
      await this.setupAudioSource();
      this.isActive = true;
      this.animate();
    } catch (error) {
      console.error("Error starting audio visualizer:", error);
    }
  }

  public stop(): void {
    this.isActive = false;
    if (this.animationId !== null) {
      globalAnimationId = "";
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.cleanupAudioNodes();
  }

  protected abstract cleanupAudioNodes(): void;

  protected getVolume(): number {
    if (!this.analyser) return 0;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(dataArray);

    const average =
      dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
    return Math.round((average / 255) * 100);
  }

  private animate(): void {
    if (!this.isActive || (globalAnimationId && globalAnimationId !== this.id)) return;
    const volume = this.getVolume();
    this.volumeData.push(volume);
    if (this.volumeData.length > 100) {
      this.volumeData.shift();
    }

    this.drawVisualization();

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  private drawVisualization(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const barWidth = 1;
    const gap = 5;
    const totalWidth = barWidth + gap;
    const maxBars = Math.floor(this.canvas.width / totalWidth);

    const borderRadius = 4;

    this.ctx.fillStyle = "rgba(73, 129, 242, 0.7)";

    // 计算 canvas 的中心线
    const centerY = this.canvas.height / 2;

    for (let i = 0; i < maxBars; i++) {
      const volume = this.volumeData[this.volumeData.length - 1 - i] || 0;
      const actualHeight = (volume / 100) * this.canvas.height;

      const height = Math.max(actualHeight, this.canvas.height / 24);

      const x = Math.round(i * totalWidth);
      // 计算矩形的 y 坐标，使其垂直居中
      const y = Math.round(centerY - height / 2);

      this.drawRoundedRect(x, y, barWidth, height, borderRadius);
    }
  }

  private drawRoundedRect(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    if (width < 2 * radius) radius = width / 2;
    if (height < 2 * radius) radius = height / 2;

    x = Math.round(x);
    y = Math.round(y);
    width = Math.round(width);
    height = Math.round(height);
    radius = Math.round(radius);

    this.ctx.beginPath();
    this.ctx.moveTo(x + radius, y);
    this.ctx.lineTo(x + width - radius, y);
    this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    this.ctx.lineTo(x + width, y + height - radius);
    this.ctx.quadraticCurveTo(
      x + width,
      y + height,
      x + width - radius,
      y + height
    );
    this.ctx.lineTo(x + radius, y + height);
    this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    this.ctx.lineTo(x, y + radius);
    this.ctx.quadraticCurveTo(x, y, x + radius, y);
    this.ctx.closePath();
    this.ctx.fill();
  }

  public drawInactiveState(): void {
    //     this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    //     const dotRadius = 1;
    //     const gap = 3;
    //     const totalWidth = dotRadius * 2 + gap;
    //     const maxDots = Math.floor(this.canvas.width / totalWidth);
    //     // 使用 canvas 的中心线
    //     const centerY = this.canvas.height / 2;
    //     for (let i = 0; i < maxDots; i++) {
    //       const x = i * totalWidth + dotRadius;
    //       // 使用 centerY 替代固定的 y 值
    //       const y = centerY;
    //       this.ctx.beginPath();
    //       this.ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
    //       this.ctx.fillStyle = "rgb(200, 200, 200)"; // 使用浅灰色
    //       this.ctx.fill();
    //     }
  }
}

export default BaseAudioVisualizer;
